import { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import '../../../App.css';
import { apiUrlPrefix } from '../../../config';
import useStore from '../state-files/zustondStoreStates';
import GetUserCommunities from '../Components/getUserCommunities';
import GetUsers from '../Components/getUsers';
import getAllNonDiscussionUsers from '../helper-methods/getAllNonDiscussionUsers';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const AddDiscussionMember = ({ closeMenu, chatId }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setName] = useState();
  const { setCommunities } = useStore();

  // Use TanStack Query to manage the user list
  const { data: usersData } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => getAllNonDiscussionUsers(setName, chatId),
    enabled: open, // Only fetch when the dialog is opened
  });

  // --- MUTATION LOGIC ---
  const addNewDiscussionMember = async ({ users }) => {
    // Since you are using 'multiple' in Autocomplete, 
    // 'users' is already the array you need!
    const apiUrl = apiUrlPrefix + 'Discussion/addDiscussionMember';
    /* 
      
      
              USERS 



              
    */
    // 1. Map the names (from Formik) back to IDs (from usersData)
    const userIds = users.map(selectedName => {
      // If 'All' is selected, handle it based on your backend logic 
      // (usually you'd send a special ID or the string 'All')
      if (selectedName === 'All') return 'All';
      if (selectedName === currentUser) return localStorage.getItem('userId');

      // Find the original user object that matches the selected name
      const foundUser = usersData.find(u => u.name === selectedName);

      // Return the ID if found, otherwise return the name as a fallback
      return foundUser ? foundUser.id : selectedName;
    });

    // 2. Extra safety check for "All"
    let finalUsers = userIds;
    if (finalUsers.length > 1 && finalUsers.includes("All")) {
      finalUsers = finalUsers.filter(id => id !== "All");
      formik.setFieldValue("users", finalUsers); // Update UI state
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Users: finalUsers,
        Id: chatId,
      }),
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Invalid credentials or permissions.');
      }
      throw new Error('Network response was not ok');
    }
    return response.json();
  };

  const addDiscussionMemberMutation = useMutation({
    mutationFn: addNewDiscussionMember,
    onSuccess: (data) => {
      if (data) {
        handleClose();
      } else {
        throw new Error('Unexpected response structure');
      }
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  // --- DIALOG CONTROL ---
  const handleClickOpen = (e) => {
    e.stopPropagation(); // Prevent Menu closing until we want it to
    setOpen(true);
  };

  const handleClose = () => {
    setError(null);
    setOpen(false);
    if (closeMenu) closeMenu();
  };

  const formik = useFormik({
    initialValues: {
      users: ['All'],
    },
    validationSchema: Yup.object({
      users: Yup.array()
        .of(Yup.string())
        .min(1, 'At least one user is required')
        .required('At least one user is required'),
    }),
    onSubmit: (values) => {
      setError(null);
      addDiscussionMemberMutation.mutate(values);
    },
  });

  return (
    <div>
      <PersonAddIcon
        id="addCommunityMemberButton"
        variant="contained"
        onClick={handleClickOpen}
        sx={{ color: '#6297dd', cursor: 'pointer', boxShadow: 'none' }}
      />

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add Discussion Members</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>

            <GetUsers formik={formik} setName={setName} currentUser={currentUser} />

            {/* Adding community field */}
            <GetUserCommunities formik={formik} />

            {error && (
              <Typography color="error" variant="body2" style={{ marginTop: '10px' }}>
                {error}
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={addDiscussionMemberMutation.isLoading}>
              {addDiscussionMemberMutation.isLoading ? 'Adding members...' : 'Add members'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default AddDiscussionMember;